import { useDisclosure } from '@mantine/hooks'
import { Close, Menu } from '@mui/icons-material'
import { AppBar, Box, IconButton, Toolbar, useTheme } from '@mui/material'
import { forwardRef } from 'react'

import EElement from '~/features/ui/models'
import ImpersonationBanner from '~/layouts/account/header/impersonationBanner'
import Logo from '~/layouts/account/header/logo'
import SideNav from '~/layouts/account/header/nav/side'
import EAccountGridAreas from '~/layouts/account/models'
import PortalAccount from '~/layouts/app/header/account/portal'
import ContactInfoButton from '~/layouts/app/header/account/portal/contactInfoButton'
import { store } from '~/store'
import rem from '~/utils/ui'

type TProps = {
  showContactInfoButton: boolean
}

const Header = forwardRef<HTMLElement, TProps>(
  ({ showContactInfoButton }, ref) => {
    const [isOpen, { open, close }] = useDisclosure(false)
    const theme = useTheme()
    const isSideNavPinned = store((state) => state.UIElements.isSideNavPinned)

    return (
      <>
        <AppBar
          component="header"
          ref={ref}
          id={EElement.accountHeader}
          position="static"
          sx={{
            gridArea: EAccountGridAreas.HEADER,
            minHeight: rem(1),
            backgroundColor: theme.palette.menuBackground,
            zIndex: theme.zIndex.drawer + 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'stretch',
            flexDirection: 'column',
            '&.MuiPaper-root': {
              boxShadow: 'none',
            },
          }}
        >
          <ImpersonationBanner />
          <Toolbar
            disableGutters
            sx={{
              ...theme.mixins.accountToolbar,
              width: '100%',
              paddingInline: rem(12),
              paddingBlock: { xxxs: rem(24), md: rem(12) },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: { xxxs: rem(6), md: rem(8) },
              }}
            >
              <IconButton
                aria-label="open drawer"
                onClick={isOpen ? close : open}
                sx={{
                  color: theme.palette.menuIcon,
                  width: rem(48),
                  height: rem(48),
                  display: { md: 'none' },
                }}
              >
                {isOpen ? <Close /> : <Menu />}
              </IconButton>

              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: { xxxs: 'center', md: 'flex-start' },
                  gap: { md: rem(6) },
                }}
              >
                <Logo />
                {showContactInfoButton && (
                  <Box
                    sx={{
                      display: { xxxs: 'none', md: 'flex' },
                      alignItems: 'center',
                      marginLeft: { md: isSideNavPinned ? rem(133) : 0 },
                      transition: 'margin-left 0.3s ease',
                    }}
                  >
                    <ContactInfoButton />
                  </Box>
                )}
              </Box>

              <PortalAccount />
            </Box>
          </Toolbar>
        </AppBar>
        <SideNav isOpen={isOpen} close={close} />
      </>
    )
  },
)

export default Header
